import { useState, useEffect, useCallback, useRef } from 'react';
import { filterService, SavedFilter, FilterParams } from '../services/filter/filterService';
import { useAuth } from '../context/AuthContext';

export const useFilter = (modelName: string) => {
    const { user } = useAuth();
    const [filters, setFilters] = useState<SavedFilter[]>([]);
    const [defaultFilter, setDefaultFilter] = useState<SavedFilter | null>(null);
    const [activeFilter, setActiveFilter] = useState<SavedFilter | null>(null);
    const [loading, setLoading] = useState(false);
    const appliedDefaultRef = useRef(false);

    const loadFilters = useCallback(async () => {
        if (!user) return;
        setLoading(true);
        try {
            const [filtersData, defaultData] = await Promise.all([
                filterService.getFiltersByModel(modelName),
                filterService.getDefaultFilter(modelName)
            ]);

            setFilters(filtersData);
            setDefaultFilter(defaultData);

            // Aplicar filtro predeterminado solo si:
            // 1. Existe filtro predeterminado
            // 2. No hay filtro activo
            // 3. No se ha aplicado antes en esta sesión
            // 4. auto_apply es true
            if (defaultData && !activeFilter && !appliedDefaultRef.current && defaultData.auto_apply) {
                setActiveFilter(defaultData);
                appliedDefaultRef.current = true;
            }
        } catch (error) {
            console.error('Error cargando filtros:', error);
        } finally {
            setLoading(false);
        }
    }, [user, modelName, activeFilter]);

    useEffect(() => {
        if (user) {
            loadFilters();
        }
    }, [user, loadFilters]);

    const saveFilter = async (
        filterName: string,
        parameters: FilterParams,
        options: {
            isDefault?: boolean;
            applyAfterSave?: boolean;
            autoApply?: boolean;
        } = {}
    ): Promise<SavedFilter> => {
        const { isDefault = false, applyAfterSave = false, autoApply = true } = options;
        try {
            const newFilter = await filterService.createFilter({
                model_name: modelName,
                filter_name: filterName,
                parameters,
                is_default: isDefault,
                auto_apply: autoApply,
            });
            setFilters(prev => {
                const updated = [...prev.filter(f => f.id !== newFilter.id), newFilter];
                return updated.sort((a, b) => a.filter_name.localeCompare(b.filter_name));
            });
            if (isDefault) {
                setDefaultFilter(newFilter);
                if (applyAfterSave) {
                    setActiveFilter(newFilter);
                }
            }
            return newFilter;
        } catch (error: any) {
            console.error('Error guardando filtro:', error);
            throw error;
        }
    };

    const deleteFilter = async (id: number): Promise<void> => {
        try {
            await filterService.deleteFilter(id);
            setFilters(prev => prev.filter(f => f.id !== id));
            if (activeFilter?.id === id) setActiveFilter(null);
            if (defaultFilter?.id === id) setDefaultFilter(null);
        } catch (error: any) {
            console.error('Error eliminando filtro:', error);
            throw error;
        }
    };

    const setAsDefaultFilter = async (id: number) => {
        try {
            const updated = await filterService.setAsDefault(id); // backend pone is_default=true y auto_apply=true
            setDefaultFilter(updated);
            setFilters(prev => prev.map(f => ({
                ...f,
                is_default: f.id === id,
                ...(f.id === id ? { auto_apply: true } : {})
            })));
            setActiveFilter(updated);
            appliedDefaultRef.current = true;
            return updated;
        } catch (error: any) {
            console.error('Error estableciendo filtro predeterminado:', error);
            throw error;
        }
    };

    // 🔥 CORREGIDO: Ahora al desactivar, el filtro deja de ser predeterminado
    const dismissDefaultFilter = useCallback(async () => {
        try {
            await filterService.dismissDefaultFilter(modelName);
            // El backend ya puso is_default = false y auto_apply = false
            // Limpiamos el estado local del filtro predeterminado
            setDefaultFilter(null);
        } catch (error: any) {
            console.error('Error al desactivar auto-aplicación:', error);
        }
    }, [modelName]);

    const applyFilter = useCallback((filter: SavedFilter | null) => {
        setActiveFilter(filter);
    }, []);

    const clearFilter = useCallback(() => {
        setActiveFilter(null);
    }, []);

    const resetFilters = useCallback(() => {
        setActiveFilter(null);
        appliedDefaultRef.current = true;
    }, []);

    const getActiveFilterParams = useCallback((): FilterParams => {
        return activeFilter?.parameters || {};
    }, [activeFilter]);

    return {
        filters,
        defaultFilter,
        activeFilter,
        loading,
        loadFilters,
        saveFilter,
        deleteFilter,
        setAsDefaultFilter,
        dismissDefaultFilter,
        applyFilter,
        clearFilter,
        resetFilters,
        getActiveFilterParams,
        hasFilters: filters.length > 0,
        isFilterActive: !!activeFilter,
        activeFilterName: activeFilter?.filter_name || null
    };
};